// The Dream official rules and "How to enter for free" text of the embed
// (src/views/dream-rules.js), filled from the public rules answer
// (GET /api/rewards/dream/rules/{school_id}/) instead of the fan's summary. The
// entry limit, free entry and multiplier days lines are worded here; the embed may differ.
import { formatDisplayNumber } from './dreamSummary';

/** "$5", "$2.50": the period's dollars per entry. */
export function money(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '$5';
  return '$' + (Math.round(n * 100) / 100).toFixed(2).replace(/\.00$/, '');
}

/** "October 6, 2026"; "" for a missing or unreadable date. */
export function longDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

/** "24,000": the cap of one member in a prize draw. */
function capNumber(value) {
  return Math.round(Number(value)).toLocaleString('en-US');
}

const DEFAULT_ENTRY_CAP = 24000;

/** What both texts fill in: program, school, period window, prizes, whether streaks are on and the campaigns. */
export function describeRules(rules) {
  const period = rules && rules.period;
  const prizes = (rules && rules.prizes) || [];
  // Without a period the text names the defaults a new period starts with, $5 and 24,000.
  const cap = period ? (period.entry_cap != null ? Number(period.entry_cap) : NaN) : DEFAULT_ENTRY_CAP;
  return {
    program: String((rules && rules.program_name) || '').trim() || 'Homecrowd',
    school: String((rules && rules.school_name) || '').trim(),
    hasPeriod: Boolean(period),
    periodName: period ? period.name : 'the current prize draw',
    starts: period ? longDate(period.starts_at) : '',
    ends: period ? longDate(period.ends_at) : '',
    entryDollars: money(period ? period.entry_dollars : 5),
    cap: Number.isFinite(cap) ? capNumber(cap) : null,
    prizes,
    terms: prizes.length ? String(prizes[0].terms || '').trim() : '',
    hasStreaks: Boolean(rules && Array.isArray(rules.streak_milestones) && rules.streak_milestones.length),
    entryCampaigns: ((rules && rules.entry_campaigns) || []).filter((c) => CAMPAIGN_WAYS[c.trigger_type]),
  };
}

// What a Dream entries campaign of each trigger gives, as the rules name it.
const CAMPAIGN_WAYS = {
  signup: 'for signing up',
  daily_login: 'once a day',
  referral_signup: 'for each friend you refer who verifies their email',
  survey_complete: 'for completing a survey',
};

/** "5 for signing up, 1 once a day and 2 for each friend you refer who verifies their email". */

function campaignWays(campaigns) {
  const ways = campaigns.map((c) => formatDisplayNumber(c.entries) + ' ' + CAMPAIGN_WAYS[c.trigger_type]);
  return ways.length > 1 ? ways.slice(0, -1).join(', ') + ' and ' + ways[ways.length - 1] : ways[0];
}

const p = (text) => ({ kind: 'p', text });
// A paragraph with one link in it; `text` is the whole of it as plain text.
const pLink = (before, link, after) => ({ kind: 'p', text: before + link.text + after, before, link, after });
const list = (items, ordered = false) => ({ kind: 'list', items, ordered });

function howToEnterItems(ctx) {
  const items = [
    'Shop in-network with your linked card, or shop in-app, at a participating partner.',
    'Each qualifying purchase earns one entry for every ' +
      ctx.entryDollars +
      ' of that purchase, rounded down. Entries are never pooled across separate purchases.',
  ];
  if (ctx.cap != null) {
    items.push(
      'Each member can hold up to ' +
        ctx.cap +
        ' entries in this prize draw. Entries beyond that limit are not counted.'
    );
  }
  items.push(
    'On multiplier days announced in the app, the entries a qualifying purchase earns are multiplied as shown. Free entries are not multiplied.'
  );
  if (ctx.hasStreaks) {
    items.push(
      'Bonus entries are added when you make qualifying purchases on consecutive days, as shown in the app.'
    );
  }
  if (ctx.entryCampaigns.length) {
    items.push(
      'You can also get entries without a purchase: ' + campaignWays(ctx.entryCampaigns) + ', as shown in the app.'
    );
  }
  items.push(
    'Purchases that are refunded, reversed or disqualified by the partner forfeit the entries they earned.'
  );
  return items;
}

/** The official rules: an intro line, then numbered sections of paragraphs and lists. */
export function rulesDocument(ctx) {
  const sponsor = ctx.program + (ctx.school ? ' at ' + ctx.school : '');
  const window_ =
    ctx.starts && ctx.ends ? ctx.starts + ' through ' + ctx.ends : 'the dates shown in the app';
  const prizeItems = ctx.prizes.length
    ? ctx.prizes.map(
        (pz) =>
          pz.title + (pz.description && pz.description !== pz.title ? ' — ' + pz.description : '')
      )
    : ['The prizes shown on the prize draw page.'];

  const sections = [
    {
      title: '1. Sponsor',
      blocks: [
        p(
          'This prize draw is run by ' +
            sponsor +
            ' (the "Sponsor") through the ' +
            ctx.program +
            ' app.'
        ),
      ],
    },
    {
      title: '2. Eligibility',
      blocks: [
        list([
          'Open to registered ' +
            ctx.program +
            ' members who are 18 years of age or older at the time of entry.',
          'Your account must be in good standing and linked to ' +
            (ctx.school || 'your school') +
            '.',
          'Employees of the Sponsor and their immediate family members are not eligible.',
          'Void where prohibited by law.',
        ]),
      ],
    },
    {
      title: '3. Promotion period',
      blocks: [
        p(
          (ctx.hasPeriod ? 'The ' + ctx.periodName + ' prize draw' : 'The current prize draw') +
            ' runs ' +
            window_ +
            '. Entries are counted at the time of purchase, not when the purchase settles.'
        ),
      ],
    },
    {
      title: '4. How to enter',
      blocks: [
        list(howToEnterItems(ctx)),
        pLink(
          'You may also enter without making a purchase. See ',
          { text: 'How to enter for free', href: '#free-entry' },
          '.'
        ),
      ],
    },
    {
      title: '5. Prizes',
      blocks: [
        list(prizeItems),
        p(
          'Prizes are awarded as described and have no cash value. No substitution or transfer is permitted, except that the Sponsor may substitute a prize of equal or greater value if a prize becomes unavailable.'
        ),
      ],
    },
    {
      title: '6. Winner selection',
      blocks: [
        list([
          'After the promotion period closes, winners are selected at random from all entries. Each entry is one chance to win.',
          'One prize per member per prize draw.',
          'Winners are notified in the app and by the email on their account. A winner who does not respond within 7 days may forfeit the prize and an alternate winner may be drawn.',
          'Odds of winning depend on the total number of entries received.',
        ]),
      ],
    },
    {
      title: '7. General conditions',
      blocks: [
        list([
          'Taxes on prizes are the responsibility of the winner.',
          'By entering you agree to these rules.',
          'The Sponsor may disqualify any entry it believes was obtained through fraud or abuse of the program.',
          'If a prize is a ticket or an experience, the event date and venue are set by the Sponsor and are not exchangeable.',
        ]),
      ],
    },
  ];
  if (ctx.terms) {
    sections.push({ title: '8. Prize-specific terms', blocks: [p(ctx.terms)] });
  }
  return {
    intro:
      'No purchase necessary. A purchase will not increase your chances of winning beyond the entries described below.',
    sections,
  };
}

// The free entry page's pointer to the mail-in method of the official rules.
export const MAIL_IN_NOTE =
  'You can also enter by mail with a 3-by-5-inch card. Section 3 of the official rules lists what to write on the card, where to send it and how many entries each card adds.';

/** "How to enter for free": an intro line, the request steps, the mail-in pointer and what happens next. */
export function freeEntryDocument(ctx) {
  return {
    intro:
      'No purchase is necessary to enter ' +
      (ctx.hasPeriod ? 'the ' + ctx.periodName + ' prize draw' : ctx.periodName) +
      '. A free entry counts exactly like an entry earned by shopping.',
    sections: [
      {
        title: 'To request a free entry',
        blocks: [
          list(
            [
              'Be a registered ' +
                ctx.program +
                ' member' +
                (ctx.school ? ' linked to ' + ctx.school : '') +
                '.',
              'Send a message through Support in the app with the subject "Free entry", your full name, the email on your account and the name of the prize draw (' +
                ctx.periodName +
                ').',
              'One request per member per day. Each accepted request adds 50 entries to your total for this prize draw' +
                (ctx.cap != null ? ', until you hold ' + ctx.cap + ' entries in it.' : '.'),
              'Requests must be received before the prize draw closes' +
                (ctx.ends ? ' on ' + ctx.ends : '') +
                '.',
            ],
            true
          ),
        ],
      },
      {
        title: 'Free entry by mail',
        blocks: [p(MAIL_IN_NOTE)],
      },
      {
        title: 'What happens next',
        blocks: [
          p(
            'Our team adds the entries to your account, usually within two business days, and they appear in your entries count in the app. Free entries are drawn alongside every other entry.'
          ),
        ],
      },
    ],
  };
}
